/**
 * Parts of the Pricing page (PricingSlot.jsx) of the Sept 2026 redesign: the package's
 * site/pricing/index.html `.pricing-section` band (tint, 1168px measure, free-listing strip)
 * and the `.comparison` table ("The details, side by side"), plus the page's FAQ copy.
 *
 * Every number comes from the tier rows the page passes in (GET /api/slots/tiers through
 * usePricingTiers, or the published list prices when the catalogue is not published) and
 * the lib/slotPricing maths; nothing here hard-codes a price, a slot count or a coaster
 * quantity. Coaster quantities are per venue, per month (owner decision, 2026-09-27); the
 * multi-venue discount applies to quarterly billing (Standard, Premium) and to Venue
 * Sponsor, never to annual prepay, and the two are never described as stacking.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button, Eyebrow, Heading, SiteImage } from '@/components/site';
import { discountBandsText } from '@/components/site/pricing';
import { hasAnnual, monthsFreeText, paidTiers } from '@/components/site/pricing/pricingModel';
import { FREE_LISTING_CLOSED_NOTE, useFreeListingState } from '@/lib/inventory';
import { annualMonthsFree, formatCents, formatCount, quarterlyCents } from '@/lib/slotPricing';
import { PAGE_BUTTON } from './audienceParts';

// ---------------------------------------------------------------------------
// The pricing band
// ---------------------------------------------------------------------------

// The package's `.pricing-section` tint (a warm beige between rule and paper, with darker
// 1px rules above and below): rule mixed 55% into paper, solid so the canvas grid does not
// show through; navy at 15% for the edges. On it: brand-muted 4.72:1, navy 12.1:1, navy/80
// 7.14:1, the rust focus ring 4.81:1.
const PRICING_TINT = 'bg-[color-mix(in_srgb,theme(colors.brand.rule)_55%,theme(colors.brand.paper))]';

/**
 * The page hero inside the band (the package's `.pricing-section .section-heading`): 40px
 * from the band's top edge, centred from 800px and left-aligned on phones, the h1 14px
 * under the eyebrow and 17px over a 17px lede (PageHero's interior defaults are 20px / 26px
 * and a 19px lede). Descendant overrides, so PageHero itself stays as it is.
 */
export const PRICING_HERO = [
  'pt-4',
  '[&>div]:text-left nav:[&>div]:text-center [&_p]:ml-0 nav:[&_p]:ml-auto',
  '[&_h1]:mb-[17px] [&_h1]:mt-3.5 [&_p]:text-[1.0625rem]',
].join(' ');

/**
 * The tinted pricing band that opens the page (10px under the header offset, as the
 * package's `.page-main .pricing-section`). Holds the page hero and the calculator block.
 * Props: `className`, `children`.
 */
export function PricingBand({ className, children }) {
  return (
    <div data-pricing-band="" className={cn('site-light mt-2.5 border-y border-navy/15 text-navy', PRICING_TINT, className)}>
      {children}
    </div>
  );
}

/**
 * The band's content measure (`.pricing-section`: 1168px, page gutter at the sides), with
 * the band's bottom padding (65px phones, 90px from 800px). The negative top margin takes
 * the hero's bottom padding down to the package's heading margin (30px / 44px).
 * Props: `className`, `children`.
 */
export function PricingMeasure({ className, children }) {
  return (
    <div
      className={cn(
        'mx-auto -mt-3.5 w-full max-w-canvas px-gutter pb-[65px] nav:-mt-5 nav:pb-[90px]',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The free-listing strip under the plan cards (`.free-listing`): rules above and below,
 * copy on the left, "Start with a free listing" on the right from 800px (stacked below).
 * Its note is 14px at full strength (brand-muted, 4.72:1 on the band; upgrade brief §2.6).
 * The heading is an h2 (the page's outline: h1, plan cards h2, this h2) at the interior h3
 * size, as `.page-main h3` sizes it in the package.
 */
export function FreeListingStrip() {
  const titleId = useId();
  // Upgrade brief §2.8: while no venue takes free listings, say so (never promise three venues).
  const freeListing = useFreeListingState();
  return (
    <section
      aria-labelledby={titleId}
      className="mt-7 flex flex-col items-start gap-6 border-y border-navy/25 py-7 nav:mt-[42px] nav:flex-row nav:items-center nav:justify-between nav:gap-[35px]"
    >
      {/* Upgrade brief §3.5 (P2): a small spot photo of a free listing, from 800px. */}
      <SiteImage
        id="PRC-FREE-01"
        ratio="4x3"
        decorative
        sizes="200px"
        className="hidden w-[200px] shrink-0 border border-brand-rule nav:block"
      />
      <div className="min-w-0 nav:flex-1">
        <Eyebrow>Start with a hello</Eyebrow>
        <Heading id={titleId} level={2} size="card" className="my-[11px]">
          A free listing is a good first step.
        </Heading>
        <p className="m-0 text-sm leading-[1.55] text-brand-muted" data-free-listing-note="" data-state={freeListing}>
          {freeListing === 'closed'
            ? FREE_LISTING_CLOSED_NOTE
            : <>{formatCents(0)}, no card required. One offer at up to three venues. No coaster placement.</>}
        </p>
      </div>
      <Button to="/free-listing" variant="secondary" className={cn(PAGE_BUTTON, 'shrink-0')}>
        Start with a free listing
      </Button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// The plan comparison
// ---------------------------------------------------------------------------

const FALLBACK_NAMES = { standard: 'Standard', premium: 'Premium', sponsor: 'Venue Sponsor' };

/** The plan's display name (the API's, else the product name). */
export const planName = (tier) => (typeof tier.name === 'string' && tier.name.trim()) || FALLBACK_NAMES[tier.tier_key] || tier.tier_key;

const count = (n) => (Number(n) > 0 ? formatCount(n) : '');

/** "Printed placement" and "Digital presence": the package's wording (copy audit: accurate). */
const PLACEMENT = { standard: 'Logo tile on back', premium: 'Full front side', sponsor: 'Both sides' };
const DIGITAL = {
  standard: 'Profile + offer',
  premium: 'Featured offer + landing page + lead form',
  sponsor: 'Top banner + up-to-three-page microsite',
};

/** "Monthly allocation" (per venue, per month): coasters_per_venue from the tier. */
function allocationText(tier) {
  const n = count(tier.coasters_per_venue);
  if (tier.tier_key === 'standard') return n ? `On ${n} shared Premium coasters` : 'On the shared Premium coasters';
  return n ? `${n} coasters` : 'Your own coasters';
}

/** "Annual prepay": the yearly price and its months free; Venue Sponsor is quarterly only. */
function annualText(tier) {
  if (tier.tier_key === 'sponsor') return 'Not offered: billed quarterly';
  if (!hasAnnual(tier)) return 'Not offered';
  const free = annualMonthsFree(tier);
  return `${formatCents(tier.annual_price_cents)} a year${free ? `, ${monthsFreeText(free)}` : ''}`;
}

/**
 * The comparison rows for the paid tiers on screen, in display order:
 * [{ key, label, cells: [{ key: tierKey, text }] }]. Numbers come from the tier rows
 * (slots_per_venue, coasters_per_venue, monthly / quarterly / annual prices).
 */
export function comparisonRows(tiers) {
  const plans = paidTiers(tiers);
  const row = (key, label, text) => ({ key, label, cells: plans.map((t) => ({ key: t.tier_key, text: text(t) })) });
  return [
    row('placement', 'Printed placement', (t) => PLACEMENT[t.tier_key] || ''),
    row('allocation', 'Monthly allocation', allocationText),
    row('digital', 'Digital presence', (t) => DIGITAL[t.tier_key] || ''),
    row('slots', 'Businesses per venue in this tier', (t) => count(t.slots_per_venue) || '—'),
    row('monthly', 'Monthly price', (t) => formatCents(t.monthly_price_cents)),
    row('quarterly', 'Paid each quarter', (t) => `${formatCents(quarterlyCents(t))} a quarter`),
    row('annual', 'Annual prepay', annualText),
    row('term', 'Minimum term', (t) => (t.tier_key === 'sponsor' ? '12-month commitment' : '3 months')),
  ];
}

/**
 * True while the element's content is wider than its box (the table scrolls sideways).
 * Re-checked when the box resizes (ResizeObserver) or the window does.
 */
function useSidewaysScroll(ref) {
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setScrolls(el.scrollWidth > el.clientWidth + 1);
    check();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(check);
      observer.observe(el);
    }
    window.addEventListener('resize', check);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [ref]);
  return scrolls;
}

// Cells: the package's 19px / 22px padding from 800px (16px on phones), rule lines between
// rows. The first column sticks to the left edge while the table scrolls on phones.
//
// Enlarged text on a phone (an em query: under 20em, i.e. under 320px at 16px text, 640px
// at 32px): the rem sizes would make the pinned labels as wide as the box (8.5rem = 272px
// at 200%) and each plan column wider still. There the columns are sized from the box
// instead (container query units: the scroll box is an inline-size container): labels 45%
// of the box, each plan about 50%, so one whole plan column is always readable beside the
// labels and the next one peeks in; the side padding drops to 0.25rem and long words
// hyphenate (breaking anywhere only as a last resort).
// (Class names are written out in full: Tailwind only generates what it finds verbatim.)
const CELL = cn(
  'border-b border-brand-rule px-4 py-4 text-left align-top font-medium nav:px-[22px] nav:py-[19px]',
  '[@media(max-width:19.9375em)]:px-1 [@media(max-width:19.9375em)]:py-3',
  '[@media(max-width:19.9375em)]:hyphens-auto [@media(max-width:19.9375em)]:[overflow-wrap:anywhere]',
);
const STICKY = cn(
  'sticky left-0 z-[1] w-[8.5rem] min-w-[8.5rem] border-r nav:w-[28%] nav:min-w-0 nav:border-r-0',
  '[@media(max-width:19.9375em)]:w-[45cqi] [@media(max-width:19.9375em)]:min-w-[45cqi] [@media(max-width:19.9375em)]:max-w-[45cqi]',
);
// The table's own minimum width: labels + three plan columns (compact: 45% + 3 × 50% of the box).
const TABLE_MIN = 'min-w-[40rem] nav:min-w-[43.75rem] [@media(max-width:19.9375em)]:min-w-[195cqi]';

function ComparisonTable({ tiers }) {
  const captionId = useId();
  const hintId = useId();
  const scrollRef = useRef(null);
  const scrolls = useSidewaysScroll(scrollRef);
  const plans = paidTiers(tiers);
  const rows = comparisonRows(tiers);
  return (
    <>
      {scrolls ? (
        <p id={hintId} className="mb-3 text-[0.8125rem] text-brand-muted" data-compare-hint="">
          Scroll sideways to see every plan <span aria-hidden="true">→</span>
        </p>
      ) : null}
      <div
        ref={scrollRef}
        role="region"
        aria-labelledby={captionId}
        aria-describedby={scrolls ? hintId : undefined}
        // Focusable only while it scrolls, so keyboard users can scroll it (axe
        // scrollable-region-focusable) without an extra tab stop on wide screens. The global
        // focus ring covers controls only, so the box draws the rust ring itself (5.26:1 on
        // the section's BAND_TINT).
        tabIndex={scrolls ? 0 : undefined}
        className="overflow-x-auto border border-brand-rule bg-brand-sheet [container-type:inline-size] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px] focus-visible:outline-brand-rust"
        data-compare=""
      >
        <table className={cn('w-full border-separate border-spacing-0 text-[0.9375rem] leading-[1.55] [&_tr:last-child>*]:border-b-0', TABLE_MIN)}>
          <caption id={captionId} className="sr-only">
            {`${plans.map(planName).join(', ')} compared, per venue`}
          </caption>
          <thead>
            <tr>
              <th scope="col" className={cn(CELL, STICKY, 'border-navy bg-navy text-brand-sheet')}>Per venue</th>
              {plans.map((t) => (
                <th key={t.tier_key} scope="col" className={cn(CELL, 'border-navy bg-navy text-brand-sheet')}>
                  {planName(t)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-compare-row={r.key}>
                <th scope="row" className={cn(CELL, STICKY, 'bg-brand-sheet')}>{r.label}</th>
                {r.cells.map((c) => (
                  <td key={c.key} className={cn(CELL, 'font-normal')} data-compare-cell={`${r.key}-${c.key}`}>
                    {c.text}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ComparisonLoading() {
  return (
    <div role="status" data-compare-loading="">
      <span className="sr-only">Loading plan details…</span>
      <div aria-hidden="true" className="border border-brand-rule bg-brand-sheet">
        <div className="h-[3.75rem] bg-navy" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-6 border-b border-brand-rule px-4 py-5 last:border-b-0 nav:px-[22px]">
            <div className="h-4 w-1/4 bg-navy/10 motion-safe:animate-pulse" />
            <div className="h-4 flex-1 bg-navy/10 motion-safe:animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The plan comparison (`.table-scroll` + `.comparison`): a real table, one column per paid
 * tier on screen, one row per fact, per venue. States from usePricingTiers: loading (a
 * table-shaped placeholder, announced), error (message + "Try again" → `onRetry`; no
 * prices are shown), else the table (live tiers, or the published list prices).
 * Phones: the table keeps its columns and scrolls sideways inside its box (never the
 * page), with the row labels pinned and a "Scroll sideways" hint while it overflows.
 *
 * Props: `tiers`, `status` ('loading' | 'ready' | 'fallback' | 'error'), `onRetry`.
 */
export function PlanComparison({ tiers, status, onRetry }) {
  const shown = paidTiers(tiers);
  if (status === 'error') {
    return (
      <div role="alert" className="border border-brand-rule bg-brand-sheet p-6 text-center nav:p-10" data-compare-error="">
        <p className="m-0 text-base">We couldn&apos;t load the plan details just now.</p>
        <Button variant="secondary" arrow="↺" className="mt-5" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  if (!shown.length) return <ComparisonLoading />;
  return <ComparisonTable tiers={tiers} />;
}

/**
 * The small print under the table (`.page-fine`): what the allocations mean, and the
 * multi-venue discount rule in the words of lib/slotPricing's bands. 14px at full strength
 * (brand-muted, 5.18:1 on BAND_TINT; upgrade brief §2.6).
 * Props: `tiers` (the tier rows on screen; [] while none are shown: no figure then).
 */
export function ComparisonFine({ tiers }) {
  const standard = paidTiers(tiers).find((t) => t.tier_key === 'standard');
  const n = standard ? count(standard.coasters_per_venue) : '';
  return (
    <div className="mt-[18px] max-w-[66ch] space-y-2 text-sm leading-[1.65] text-brand-muted" data-compare-fine="">
      <p className="m-0">
        Allocations are per venue, per month, printed every quarter. Standard tiles share Premium coasters; they do
        not add {n ? `another ${n}` : 'more'} printed coasters per advertiser. Actual availability and delivery timing
        are confirmed before purchase.
      </p>
      <p className="m-0">Only one Venue Sponsor per venue.</p>
      <p className="m-0">
        Quarterly billing takes {discountBandsText()}, Venue Sponsor included. Annual prepay has no multi-venue
        discount.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

/**
 * The page's questions (the package's four, corrected: annual prepay and the multi-venue
 * discount never combine; the Venue Sponsor early-exit rule as the Terms state it), plus
 * the category rule. `freeText` = the annual saving in words from the tiers on screen
 * ("2 months free", "1 month free", "up to 2 months free", or '' when there is none).
 */
export function pricingFaq(freeText) {
  return [
    {
      q: 'What do I pay upfront?',
      a: 'Quarterly plans are paid three months at a time. Annual Standard and Premium plans are prepaid for the year; Venue Sponsor is always billed quarterly. The calculator shows the payable amount alongside the price.',
    },
    {
      q: 'How do multi-venue discounts work?',
      a: `Quarterly billing takes ${discountBandsText()}, Venue Sponsor included. Venues are counted across all your paid slots, whatever the tier. Annual prepay has no multi-venue discount${freeText ? `: it already includes ${freeText}, and the two never combine` : ''}.`,
    },
    {
      q: 'Can I cancel Venue Sponsor early?',
      a: 'Venue Sponsor is a 12-month commitment billed quarterly. You can come off early, but the rest of the 12 months is still billed, and your multi-venue discount stays at the rate you started on.',
    },
    {
      q: 'Can another business in my category take a slot at the same venue?',
      a: 'No. There is one paid advertiser per category at each venue: one realtor, one gym, one dentist. It is first come, first served, and renewing on time keeps your category.',
    },
    {
      q: 'Does a free listing include coasters?',
      a: 'No. A free listing is a digital business listing and one offer at up to three selected venues. Printed placement comes with a paid slot.',
    },
  ];
}
